import { useEffect, useRef } from 'react'
import { shallowEqual } from 'react-redux'

import { setOptimizelyExperiment } from 'actions/SystemActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isReady, preloaders, userContext } from 'analytics/optimizely/optimizely'

export type OptimizelyExperiments = OptimizelyClientSideExperiments | OptimizelyServerSideExperiments;

export enum OptimizelyClientSideExperiments {
  trendingDestinationsButtonCopyValue = 'trending_destinations',
  upgradeModalVariant = 'upgrade_modal_inclusions',
  lereStickyAbandonedCartEnabled = 'lere_sticky_ac_a_b_test',
  lereHomepageHighIntentCarouselEnabled = 'lere_high_intent_carousel_a_b_test',
  lereSimilarOffersGraphModelEnabled = 'lere_similar_offers_reco_using_graph_model_on_offer_page',
  lereNewHomeAlternativeOffersEnabled = 'lere_new_hp_personalised_alternatives_vs_plm',
  removeExperiencesFromCheckout = 'cro_remove_experiences_from_checkout',
  searchDynamicEv = 'search_dynamic_ev_test_2',
  searchPriceMapMarkers = 'search_price_map_markers',
  loginStartTest = 'cro_sign-up_modal_test',
  searchShowLoggedOutPrices = 'search_show_logged_out_prices',
  searchShowMoreInclusions = 'search_show_more_inclusions',
  isTourReviewsEnabled = 'cro_tours_reviews',
  isNortonButtonEnabled = 'cro_norton_button',
  clientSideExperimentAATest = 'aa_test_client_18_10',
}

export enum OptimizelyServerSideExperiments {
  isNewHomePageTestEnabled = 'cro_new_homepage_v2',
  isAuNewHomePageTestEnabled = 'cro_new_homepage_v2_au',
  googleSignInDelay = 'cro_desktop_google_sign-in_modal',
  lerePropertySearchEnabledWithDates = 'lere_property_search_with_dates',
  isNewLandingPageTestEnabled = 'cro_new_landing_page',
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  serverSideExperimentAATest = 'aa_test_server_18_10',
  isTpfmCarouselEnabled = 'lere_hotels_vertical_personalised_alternatives_test'
}

export function activateOptimizelyExperiment(flagKey: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  // datafile can take a few seconds to load, if it's not ready, add the flag to the preloaders array
  if (!isReady) {
    preloaders.push(flagKey)
    return
  }
  if (!userContext) return

  try {
    const decision = userContext.decideForKeys([flagKey])[flagKey]
    const experimentValues = {
      variationKey: decision.variationKey,
      variables: decision.variables,
      enabled: decision.enabled,
    }

    return experimentValues
  } catch (error) {
    console.error(error)
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.config.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in (think of this as
 * the same thing as the number variant previously used in Optimize).  Each experiment also has a 'variables'value
 * that can be used to pass variables from Optimizely to use in the experiment.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experiment = useAppSelector(state => state.config.optimizelyExperiments[experimentId], shallowEqual)
  const experimentActivated = useRef(false)
  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      dispatch(setOptimizelyExperiment(experimentId))
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  if (experiment?.enabled && canTriggerExperiment) {
    return experiment.variationKey
  }
}

export function isClientSideExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments): experimentId is OptimizelyClientSideExperiments {
  return Object.values(OptimizelyClientSideExperiments).includes(experimentId as OptimizelyClientSideExperiments)
}

export function useOptimizelyExperimentVariable(experimentId: OptimizelyClientSideExperiments): any | undefined {
  const experiment = useAppSelector(state => state.config.optimizelyExperiments[experimentId])
  if (experiment?.enabled) {
    return experiment.variables[experiment.variationKey]
  }
}
